import { Flex, Table } from 'components';
import { AreaPathChips } from 'components/AreaPathChips/AreaPathChips';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPeopleGroup,
  faEdit,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { faPaperPlane } from '@fortawesome/free-regular-svg-icons';
import { Button, Select, TextField } from 'components/_form';
import { SendMessageModal } from './SendMessageModal';
import { useTranslation } from 'react-i18next';
import { TablePagination } from 'components/Table/TablePagination';
import { ITeam } from 'types/team';
import { useAreasQuery } from 'hooks/queries/useAreasQuery';
import { useNavigate } from 'react-router-dom';
import { getUserDisplayLabel } from 'utilities/fullName';

const Icon = styled(Flex)<{ isActive?: boolean }>`
  justify-content: center;
  ::after {
    content: '';
    width: 15px;
    height: 15px;
    border-radius: 15px;
    background-color: ${({ isActive }) => (isActive ? 'green' : 'grey')};
  }
`;

const Logo = styled(Flex)`
  justify-content: center;
  align-items: center;
  font-size: 30px;
  margin: 0 auto;
  padding: 2px;
  width: fit-content;
  ${({ theme }) => `
  background-color: ${theme.palette.primary.dark};
  color: ${theme.palette.neutral.light};
  `};
`;

const Buttons = styled(Flex)`
  width: fit-content;
  margin: 0 auto;
  gap: 10px;
`;

interface Props {
  teams: ITeam[];
  setSelectedItemData: (data: ITeam) => void;
  setOpenAddModal: (open: boolean) => void;
}

const TeamsAndGroupsTable: React.FC<Props> = ({
  teams,
  setSelectedItemData,
  setOpenAddModal
}) => {
  const { t } = useTranslation();
  const { data: areasData = [] } = useAreasQuery();
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(25);
  const [search, setSearch] = useState<string>('');
  const [openSendModal, setOpenSendModal] = useState<boolean>(false);

  const areaLabelById = useMemo(
    () =>
      new Map(
        areasData.map((area) => [
          String(area.id),
          area.materialized_path_label
        ])
      ),
    [areasData]
  );

  const filteredTeams = useMemo(() => {
    if (!search.trim()) return teams;
    const q = search.toLowerCase();
    return teams.filter((team) => {
      const leaderLabel = getUserDisplayLabel({
        first_name: (team.leader as any).first_name,
        last_name: (team.leader as any).last_name,
        email: team.leader.email,
        username: team.leader.username
      }).toLowerCase();
      const areasStr = team.areas
        .map((a) => areaLabelById.get(String(a.id)))
        .join(' ')
        .toLowerCase();
      return (
        (team.name ?? '').toLowerCase().includes(q) ||
        String(team.id_code ?? '').toLowerCase().includes(q) ||
        leaderLabel.includes(q) ||
        (team.leader.email ?? '').toLowerCase().includes(q) ||
        (team.leader.username ?? '').toLowerCase().includes(q) ||
        areasStr.includes(q)
      );
    });
  }, [teams, search, areaLabelById]);

  const logo = (
    <Logo>
      <FontAwesomeIcon icon={faPeopleGroup} />
    </Logo>
  );

  const buttons = (item: ITeam) => (
    <Buttons>
      <Button
        variant="blue"
        icon={<FontAwesomeIcon icon={faPaperPlane} />}
        onClick={() => {
          setOpenSendModal(true);
        }}
      />
      <Button
        variant="eucalyptus"
        icon={<FontAwesomeIcon icon={faEdit} />}
        onClick={() => {
          // setOpenAddModal(true);
          // setSelectedItemData(item);

          navigate(`teams/${item.id}`);
        }}
      />
    </Buttons>
  );

  const header: string[] = [
    t('employeesAndUsersView.tableHeaders.active'),
    t('employeesAndUsersView.tableHeaders.id'),
    t('employeesAndUsersView.tableHeaders.logo'),
    t('employeesAndUsersView.tableHeaders.teamName'),
    t('employeesAndUsersView.tableHeaders.leader'),
    t('employeesAndUsersView.tableHeaders.area'),
    ''
  ];

  const data = useMemo(
    () =>
      filteredTeams.map((team) => ({
        active: <Icon isActive={team.state === 'active'} key={`active-${team.id}`} />,
        id: team.id,
        logo: team.logo_url ? (
          <img
            width="50px"
            src={process.env.REACT_APP_API_URL + team.logo_url}
            alt="logo"
          />
        ) : (
          logo
        ),
        name: team.name,
        leader: getUserDisplayLabel({
          first_name: (team.leader as any).first_name,
          last_name: (team.leader as any).last_name,
          email: team.leader.email,
          username: team.leader.username
        }) || team.leader.email,
        area: (
          <AreaPathChips
            chips={team.areas
              .map((a) => {
                const label = areaLabelById.get(String(a.id));
                return label?.trim()
                  ? { key: `team-${team.id}-area-${a.id}`, label }
                  : null;
              })
              .filter(
                (c): c is { key: string; label: string } => c != null
              )}
          />
        ),
        edit: buttons(team)
      })),
    [filteredTeams, logo, buttons, areaLabelById]
  );

  const paginatedData = useMemo(() => {
    const start = (page - 1) * perPage;
    return data.slice(start, start + perPage);
  }, [data, page, perPage]);

  const additionalHeader = (
    <>
      <FontAwesomeIcon icon={faSearch} />
      <TextField
        hideError
        value={search}
        onChange={(e) => setSearch(e.target.value ?? '')}
        placeholder={t('employeesAndUsersView.form.search') ?? undefined}
      />
      <Select
        placeholder={t('employeesAndUsersView.form.chooseArea') ?? undefined}
        options={[]}
        withoutMargin
      />
      <TablePagination
        hidePageInput
        page={page}
        total={filteredTeams.length}
        perPage={perPage}
        setPage={setPage}
      />
    </>
  );
  return (
    <>
      <SendMessageModal
        isOpen={openSendModal}
        onCancelClick={() => setOpenSendModal(false)}
      />
      <Table
        header={header}
        data={paginatedData}
        pagination={{
          page: page,
          setPage: setPage,
          perPage: perPage,
          total: filteredTeams.length,
          setPerPage: setPerPage
        }}
        additionalHeaderElement={additionalHeader}
      />
    </>
  );
};

export default TeamsAndGroupsTable;
